import React, { useState } from 'react';
import { Box, TextField, InputAdornment, InputBase, useTheme } from '@mui/material';
import { Search } from '@mui/icons-material';
import ProgressionTab from '../../../../../theme/ProgressionBuilder/ProgressionTab';
import Text from '../../../../../theme/text/Text';
import Shbutton from '../../../../../theme/buttons/Shbutton';
import ShTextSkeleton from '../../../../../theme/skeleton/ShTextSkeleton';
import { useTranslation } from 'react-i18next';
import { useGetEtailizeConnectionDefinitionsQuery } from '../../../../../../newapi/webshop/integratorSlice';
import { tokens } from '../../../../../../theme';
import { marketPlaceAppIconsMap, normalizeMarketPlaceName } from '../../../../../../utils/logomapper';

/**
 * EtailizeMarketplaceSelection Component
 * 
 * Displays available Etailize marketplaces in a searchable grid layout
 * and allows users to select one to proceed with authentication.
 * 
 * Props:
 * - handleDataUpdate: Function to update progression state
 * - handleNext: Function to navigate to next step
 */
const Content = (props) => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedMarketplace, setSelectedMarketplace] = useState(null);
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    
    const {
        data: marketplaces,
        isLoading,
        error
    } = useGetEtailizeConnectionDefinitionsQuery();

    // Filter marketplaces based on search term
    const filteredMarketplaces = marketplaces?.filter(marketplace =>
        marketplace.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        marketplace.description?.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

    const enrichedMarketplaces = filteredMarketplaces.map(marketplace => {
        const normName = normalizeMarketPlaceName(marketplace.name);
        const match = marketPlaceAppIconsMap.find(icon => normName.includes(icon.name));
        return {
          ...marketplace,
          Icon: match?.Icon || null,
        };
      });


    const handleMarketplaceSelect = (marketplace) => {
        setSelectedMarketplace(marketplace);
        
        // Update progression state with selected marketplace
        props.handleDataUpdate({
            etailize_marketplace: marketplace
        });
        
        // Navigate to authentication step
        props.handleNext("tabEtailizePropagation");
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    // Loading state
    if (isLoading) {
        return (
            <Box>
                <Text fontSize={18} fontWeight="bold" sx={{ mb: 3 }}>
                    {t('etailize_select_marketplace')}
                </Text>
                <TextField
                    placeholder={t('search_marketplaces')}
                    variant="outlined"
                    fullWidth
                    disabled
                    sx={{ mb: 3 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        ),
                    }}
                />
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2 }}>
                    {[1, 2, 3, 4, 5, 6].map((index) => (
                        <Box
                            key={index}
                            sx={{
                                p: 3,
                                border: '1px solid #e0e0e0',
                                borderRadius: 2,
                                height: '120px'
                            }}
                        >
                            <ShTextSkeleton />
                            <Box sx={{ mt: 1 }}>
                                <ShTextSkeleton />
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>
        );
    }

    // Error state
    if (error) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Text color="error" fontSize={18} fontWeight="bold" sx={{ mb: 2 }}>
                    {t('error_loading_marketplaces')}
                </Text>
                <Text color="textSecondary" sx={{ mb: 3 }}>
                    {t('error_loading_marketplaces_description')}
                </Text>
                <Shbutton variant="outlined" onClick={() => window.location.reload()}>
                    {t('retry')}
                </Shbutton>
            </Box>
        );
    }

    // Empty state
    if (!marketplaces || marketplaces.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Text fontSize={18} fontWeight="bold" sx={{ mb: 2 }}>
                    {t('no_marketplaces_available')}
                </Text>
                <Text color="textSecondary">
                    {t('no_marketplaces_available_description')}
                </Text>
            </Box>
        );
    }

    return (
        <Box sx={{mb:"-24px"}}>
            <Text fontSize={18} fontWeight="bold" sx={{ mb: 1 }}>
                {t('etailize_select_marketplace')}
            </Text>
            <InputBase
                // placeholder='hello'
                // placeholder={'search_marketplaces'}
                placeholder={t("search_marketplaces")} 
                value={searchTerm}
                onChange={handleSearchChange}
                sx={{
                    height:40,
                    background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                    px:2,
                    borderRadius:5,
                    width:1,
                    mb:3,
                    fontSize:14,
                    color:colors.txt["primary"]
                }}
            
            />

            {/* Marketplace grid */}
            <Box sx={{ 
                display: 'flex',
                flexDirection:'column', 
                // gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
                gap: 1.5,
                maxHeight: '300px',
                overflowY: 'auto',
                pb:1,
            }}>
                {enrichedMarketplaces.map((marketplace) => (
                    <Box
                        key={marketplace.id}
                        sx={{
                            // border: '1px solid #e0e0e0',
                            flexShrink:0,
                            background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                            border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                            boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease-in-out',
                            px:2,
                            pl:0.5,
                            borderRadius:5,
                            display:"flex",
                            alignItems:'center',
                            // justifyContent:'center',
                            height:40,
                            '&:hover': {
                                borderColor: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
                                backgroundColor: theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
                                // backgroundColor: '#f8f9fa',
                                // transform: 'translateY(-2px)',
                                // boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
                            },
                            // '&:active': {
                            //     transform: 'translateY(0px)'
                            // }
                        }}
                        onClick={() => handleMarketplaceSelect(marketplace)}
                    >
                        {marketplace.Icon &&
                            <Box
                                sx={{
                                    borderRadius:4,
                                    height:32,
                                    width:32,
                                    overflow:'hidden',
                                    mr:1.5,
                                    background:theme.palette.mode === "dark" ? colors.txt["primary"] : null,
                                }}
                            >
                                <marketplace.Icon width={32} height={32} />
                            </Box>
                        }

                        <Text >
                            {marketplace.name}
                        </Text>
                        {/* {marketplace.description && (
                            <Text 
                                fontSize={12}
                                color="textSecondary"
                                sx={{
                                    // display: '-webkit-box',
                                    // WebkitLineClamp: 2,
                                    // WebkitBoxOrient: 'vertical',
                                    // overflow: 'hidden',
                                    // lineHeight: 1.4
                                }}
                            >
                                {marketplace.description}
                            </Text>
                        )} */}
                        {/* {marketplace.country && (
                            <Text 
                                fontSize={11}
                                color="primary" 
                                sx={{ 
                                    mt: 1, 
                                    display: 'block',
                                    fontWeight: 'medium'
                                }}
                            >
                                {t('country')}: {marketplace.country}
                            </Text>
                        )} */}
                    </Box>
                ))}
            </Box>

            {/* No results message */}
            {filteredMarketplaces.length === 0 && searchTerm && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Text color="textSecondary">
                        {t('no_marketplaces_found', { searchTerm })}
                    </Text>
                </Box>
            )}
        </Box>
    );
};

const EtailizeMarketplaceSelection = (props) => {
    const { t } = useTranslation();

    return (
        <ProgressionTab 
            tabId="tabEtailizeMarketplace" 
            subtitle={t('marketplace_selection')}
            step={2} 
            maxSteps={5}
        >
            <Content {...props} />
        </ProgressionTab>
    );
};

export default EtailizeMarketplaceSelection;